// Buffer batch dei log. Specchio di `CloseYourIt::LogBuffer` (gem) e `LogBuffer`
// (dart). Accumula i log e li svuota quando: raggiunge [batchSize], scade
// [flushIntervalMs] (timer), oppure su [flushNow]/[dispose].

import type { EventPayload } from './types'

/// Massimo numero di log per singola richiesta HTTP a `/logs` (il backend rifiuta
/// batch più grandi con 413). I batch oltre soglia vengono spezzati.
export const LOGS_MAX_BATCH = 1000

/// Spezza [items] in blocchi di al più [size] elementi (ordine preservato).
export function chunkBatches<T>(items: T[], size: number): T[][] {
  if (size <= 0) return [items]
  const chunks: T[][] = []
  for (let start = 0; start < items.length; start += size) {
    chunks.push(items.slice(start, start + size))
  }
  return chunks
}

export class LogBuffer {
  private events: EventPayload[] = []
  private timer: ReturnType<typeof setInterval> | undefined
  private disposed = false

  constructor(
    private readonly onFlush: (batch: EventPayload[]) => void,
    private readonly batchSize: number = 50,
    private readonly flushIntervalMs: number = 5000,
  ) {}

  get length(): number {
    return this.events.length
  }

  add(event: EventPayload): void {
    if (this.disposed) return
    this.events.push(event)
    this.ensureTimer()
    if (this.events.length >= this.batchSize) this.flush()
  }

  /// Svuota il buffer consegnando il batch a [onFlush] (no-op se vuoto).
  flush(): void {
    if (this.events.length === 0) return
    const batch = this.events
    this.events = []
    this.onFlush(batch)
  }

  private ensureTimer(): void {
    if (this.timer !== undefined || this.flushIntervalMs <= 0) return
    this.timer = setInterval(() => this.flush(), this.flushIntervalMs)
    // In Node il timer non deve tenere vivo il processo (no-op nel browser).
    const t = this.timer as { unref?: () => void }
    if (typeof t.unref === 'function') t.unref()
  }

  /// Ferma il timer e svuota un'ultima volta. Dopo, [add] è no-op.
  dispose(): void {
    this.disposed = true
    if (this.timer !== undefined) {
      clearInterval(this.timer)
      this.timer = undefined
    }
    this.flush()
  }
}
